import {
  DATE_FORMATS,
  DATE_PRESETS,
  dateFormatLabel,
  datePresetLabel,
  effectiveRun,
  runLabel,
  suggestRun,
  type DateFormat,
  type DatePreset,
  type RunMark,
  type RunValue,
  type Step,
} from '@nolens/domain';
import { useId } from 'react';
import { cx } from './cx';
import { useLocale } from './locale';
import { uiText } from './ui-text';

const sel =
  'h-8 max-w-full rounded-full border border-border bg-surface-1 px-2.5 text-[13px] text-text-1 hover:border-text-3 focus:border-accent focus:outline-none disabled:opacity-60';
const inp =
  'h-8 min-w-0 rounded-full border border-border bg-surface-1 px-3 text-[13px] text-text-1 placeholder:text-text-3 hover:border-text-3 focus:border-accent focus:outline-none';

/** Passos que a execução automática pode fazer na página. */
export const isPageAction = (s: Step) =>
  s.kind === 'click' ||
  s.kind === 'dblclick' ||
  s.kind === 'type' ||
  s.kind === 'key' ||
  s.kind === 'select';

/** Porque é que um passo fica sempre para a pessoa (ou null). */
function forcedUser(s: Step, t: ReturnType<typeof uiText>): string | null {
  if (s.target?.sensitive) return t.forcedSensitive;
  if (!s.target) return t.forcedNoTarget;
  if (s.kind === 'select' && !s.option) return t.forcedNoOption;
  return null;
}

/**
 * Execução automática de um passo: quem o faz e, num passo de escrita, o valor
 * (pergunta no início, texto fixo ou data relativa ao dia). Ver domain/run.ts.
 */
export function RunControls({
  step,
  onChange,
}: {
  step: Step;
  onChange: (p: Partial<Step>) => void;
}) {
  const id = useId();
  const locale = useLocale();
  const t = uiText(locale);
  if (!isPageAction(step)) return null;
  const forced = forcedUser(step, t);
  const mark = effectiveRun(step);
  const needsValue = step.kind === 'type' && !step.value && !forced;
  const setValue = (value: RunValue | undefined) => {
    // Sem valor, a escrita é da pessoa; com valor novo, volta à sugestão (a extensão faz)
    const next: Partial<Step> = { value };
    if (!value && step.run === 'auto') next.run = undefined;
    onChange(next);
  };

  return (
    <div
      className="no-print mt-2 flex flex-wrap items-center gap-2 text-[13px] text-text-2"
      data-run={mark}
    >
      <label htmlFor={id + '-m'} className="font-medium">
        ⚡ {t.onRun}
      </label>
      <select
        id={id + '-m'}
        className={sel}
        disabled={!!forced || needsValue}
        value={mark}
        onChange={(e) => onChange({ run: e.target.value as RunMark })}
        title={forced ?? (needsValue ? t.setValueFirst : undefined)}
      >
        {(['auto', 'confirm', 'user'] as RunMark[]).map((m) => (
          <option key={m} value={m}>
            {runLabel(m, locale)}
            {!step.run && m === suggestRun(step) ? t.suggested : ''}
          </option>
        ))}
      </select>
      {step.kind === 'type' && !forced && (
        <>
          <label htmlFor={id + '-v'} className="font-medium">
            {t.value}
          </label>
          <select
            id={id + '-v'}
            className={sel}
            value={step.value?.kind ?? ''}
            onChange={(e) => {
              const k = e.target.value;
              if (k === 'ask') setValue({ kind: 'ask' });
              else if (k === 'text')
                setValue({ kind: 'text', text: step.typed?.trim() || t.defaultText });
              else if (k === 'date')
                setValue({ kind: 'date', preset: 'today', format: 'dd/mm/aaaa' });
              else setValue(undefined);
            }}
          >
            <option value="">{t.noValue}</option>
            <option value="ask">{t.askAtStart}</option>
            <option value="text">{t.fixedText}</option>
            <option value="date">{t.relativeDate}</option>
          </select>
          {step.value?.kind === 'ask' && (
            <input
              aria-label={t.question}
              className={cx(inp, 'w-48')}
              maxLength={100}
              placeholder={t.questionPlaceholder}
              value={step.value.label ?? ''}
              onChange={(e) =>
                setValue({ kind: 'ask', ...(e.target.value ? { label: e.target.value } : {}) })
              }
            />
          )}
          {step.value?.kind === 'text' && (
            <input
              aria-label={t.textToType}
              className={cx(inp, 'w-48')}
              maxLength={200}
              value={step.value.text}
              onChange={(e) => onChange({ value: { kind: 'text', text: e.target.value } })}
              onBlur={(e) => !e.target.value.trim() && setValue(undefined)}
            />
          )}
          {step.value?.kind === 'date' && (
            <>
              <select
                aria-label={t.date}
                className={sel}
                value={step.value.preset}
                onChange={(e) =>
                  onChange({
                    value: {
                      kind: 'date',
                      preset: e.target.value as DatePreset,
                      format: (step.value as { format: DateFormat }).format,
                    },
                  })
                }
              >
                {DATE_PRESETS.map((k) => (
                  <option key={k} value={k}>
                    {datePresetLabel(k, locale)}
                  </option>
                ))}
              </select>
              <select
                aria-label={t.dateFormat}
                className={sel}
                value={step.value.format}
                onChange={(e) =>
                  onChange({
                    value: {
                      kind: 'date',
                      preset: (step.value as { preset: DatePreset }).preset,
                      format: e.target.value as DateFormat,
                    },
                  })
                }
              >
                {DATE_FORMATS.map((f) => (
                  <option key={f} value={f}>
                    {dateFormatLabel(f, locale)}
                  </option>
                ))}
              </select>
            </>
          )}
        </>
      )}
      {forced && <span className="text-text-3">{forced}</span>}
    </div>
  );
}
